import { useQuery } from '@tanstack/react-query';

import { getPharmacy } from '@/api/pharmacies.api';
import { useActivePharmacy } from './active-pharmacy';
import { queryKeys } from '@/lib/query-keys';

/**
 * Which parts of this app exist for the branch on screen.
 *
 * These are **entitlements**, set by an admin against the pharmacy — not
 * permissions, which say what the signed-in person may do, and not the shop's
 * own operational switches, which it flips hourly. A counter-only pharmacy has
 * no order queue and no prescription work, and showing either would be offering
 * a door that is locked on the other side.
 *
 * Both default to TRUE while the record loads, so a slow request never blinks
 * the navigation out from under someone mid-shift. The server refuses these
 * routes regardless; this only decides what is worth showing.
 */
export function usePharmacyFeatures(): {
  marketplace: boolean;
  prescriptions: boolean;
  isLoading: boolean;
} {
  const { pharmacyId } = useActivePharmacy();

  const { data, isPending } = useQuery({
    queryKey: queryKeys.pharmacy(pharmacyId ?? ''),
    queryFn: () => getPharmacy(pharmacyId!),
    enabled: Boolean(pharmacyId),
    // An entitlement is not changed mid-shift; this is as static as data gets.
    staleTime: 10 * 60_000,
  });

  return {
    /*
     * On the marketplace - or taken off it with orders still to finish. A shop
     * switched off stops receiving new orders at once, but the ones a customer
     * already paid for still have to be packed, so the queue stays until they
     * are done. The server applies the same rule to the queue itself.
     */
    marketplace: data ? data.marketplaceEnabled || (data.openOrderLegs ?? 0) > 0 : true,
    prescriptions: data?.prescriptionReviewEnabled ?? true,
    isLoading: isPending,
  };
}
